import React from 'react';
import { MicOff } from 'lucide-react';

/**
 * Llamadas NO APROVECHABLES: no hubo intercambio humano suficiente para
 * determinar el desenlace, así que no hay desempeño del agente que calificar.
 *
 * Espejo de `backend/app/aprovechable.py`. La regla la decide el backend y viaja
 * en `analysis.es_aprovechable`; aquí sólo se explica. A diferencia de la
 * dirección de la llamada —que la UI puede derivar sola— esto NO se recalcula en
 * el frontend: el veredicto lo emite el LLM sobre el contenido, y una segunda
 * implementación sería una segunda regla.
 *
 * Vive en su propio componente, separado de `OutboundBoleta`, porque son dos
 * motivos distintos de no tener calificación y un supervisor necesita
 * distinguirlos: "esta llamada la marcó el agente" no es lo mismo que "en esta
 * llamada nadie habló". Colores e íconos distintos, a propósito.
 */

/** Texto de los motivos. Espejo de `aprovechable.MOTIVOS_ES`. */
export const MOTIVOS_NO_APROVECHABLE = {
  locucion_ivr: 'Sólo locución grabada; ningún agente humano habló',
  nadie_contesto: 'El agente contestó y no hubo respuesta del otro lado',
  saludo_sin_dialogo: 'Se saludaron y se cortó antes de plantear el motivo',
  ininteligible: 'Hay habla pero la transcripción no permite reconstruir nada',
  // `numero_equivocado` se retiró: un número equivocado resuelto SÍ es
  // aprovechable (hubo intercambio y desenlace). Se conserva la traducción por
  // si quedara alguna fila vieja con ese motivo en la base.
  numero_equivocado: 'Número equivocado (motivo retirado)',
};

/**
 * ¿El backend la marcó como no aprovechable?
 *
 * Lee `aprovechable_efectivo`, que el backend ya resolvió (corrección manual si
 * existe, si no el automático). La UI NO reimplementa ese COALESCE: una segunda
 * implementación sería una segunda regla, y el día que divergiera la pantalla
 * diría una cosa y los tableros otra.
 */
export function esNoAprovechable(call) {
  return call?.analysis?.aprovechable_efectivo === false;
}

/** `true` | `false` | `null` (sin clasificar todavía). */
export function aprovechamiento(analysis) {
  const v = analysis?.aprovechable_efectivo;
  return v === true || v === false ? v : null;
}

const ESTADOS = {
  true: { texto: 'Aprovechable', color: '#86efac', borde: '#4ade80', fondo: 'rgba(74, 222, 128, 0.10)' },
  false: { texto: 'No aprovechable', color: '#cbd5e1', borde: '#64748b', fondo: 'rgba(100, 116, 139, 0.14)' },
  null: { texto: 'Sin clasificar', color: '#9ca3af', borde: '#3f3f4a', fondo: 'transparent' },
};

/**
 * Estado de aprovechamiento, SIEMPRE visible en el detalle.
 *
 * Antes sólo aparecía el distintivo cuando la llamada estaba marcada como no
 * aprovechable. Con el histórico todavía sin clasificar eso significaba que no
 * se veía nada, y el control de corrección salía con las dos opciones apagadas:
 * imposible saber en qué estado estaba la llamada. "Sin clasificar" es un estado
 * real —esas llamadas siguen contando en los tableros— y tiene que decirse.
 */
export function EstadoAprovechamiento({ analysis }) {
  const estado = ESTADOS[String(aprovechamiento(analysis))];
  const corregido = analysis?.aprovechable_override != null;
  return (
    <span
      title={
        aprovechamiento(analysis) === false
          ? motivoTexto(analysis)
          : aprovechamiento(analysis) === null
            ? 'Todavía sin clasificar. Sigue contando en los tableros.'
            : 'Hubo intercambio suficiente para juzgar la llamada.'
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 999,
        border: `1px solid ${estado.borde}`,
        background: estado.fondo,
        color: estado.color,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      <MicOff size={12} />
      {estado.texto}{corregido ? ' · corregida' : ''}
    </span>
  );
}

export function motivoTexto(analysis) {
  const motivo = analysis?.motivo_no_aprovechable;
  return MOTIVOS_NO_APROVECHABLE[motivo] || 'Sin intercambio humano suficiente para juzgarla';
}

/** Distintivo para el encabezado del detalle. */
export function NoAprovechableBadge({ analysis }) {
  return (
    <span
      title={motivoTexto(analysis)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 9px',
        borderRadius: 999,
        border: '1px solid #64748b',
        background: 'rgba(100, 116, 139, 0.14)',
        color: '#cbd5e1',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
      }}
    >
      <MicOff size={12} />
      No aprovechable
    </span>
  );
}

/** Ocupa el lugar del score y de los nueve criterios. */
export function BoletaSinEvidencia({ analysis, compact = false }) {
  return (
    <div
      className="card"
      style={{
        marginTop: compact ? 0 : 16,
        borderColor: '#64748b',
        background: 'rgba(100, 116, 139, 0.07)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          color: '#cbd5e1',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <MicOff size={14} />
        Sin calificación: llamada no aprovechable
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: '#c4b5fd', margin: 0 }}>
        {motivoTexto(analysis)}. No hubo intercambio suficiente para determinar
        cómo terminó la llamada, así que no se tipifica ni se califica, y no cuenta
        en el promedio del agente ni en las tasas de spam, útiles o conversión.
      </p>
      <p style={{ fontSize: 12, lineHeight: 1.6, color: '#9ca3af', margin: '8px 0 0' }}>
        La transcripción sí está disponible abajo: es la evidencia de por qué se
        clasificó así.
      </p>
    </div>
  );
}

/**
 * Control discreto para corregir la clasificación. Sólo se le muestra a
 * administradores; el candado real es `require_admin` en el backend.
 *
 * No se le muestra al agente, y no por jerarquía: un agente que puede
 * reclasificar sus propias llamadas puede sacar del promedio las que le fueron
 * mal, y la boleta deja de significar algo.
 *
 * Vive junto al aviso de "sin calificación" y no en una pantalla de
 * administración aparte, porque la corrección se decide leyendo la
 * transcripción — que está en esta misma pantalla.
 */
export function CorregirAprovechamiento({ analysis, onCorregir, guardando = false }) {
  const efectivo = aprovechamiento(analysis);
  const corregido = analysis?.aprovechable_override != null;

  const opciones = [
    { valor: true, texto: 'Sí es aprovechable' },
    { valor: false, texto: 'No es aprovechable' },
  ];

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: '1px solid #2a2a35',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      {/* El estado actual, primero. Sin esto los dos botones salen apagados
          mientras la llamada está sin clasificar y no hay forma de saber en qué
          estado está — fue el primer comentario al probarlo. */}
      <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
        Aprovechamiento:
      </span>
      <EstadoAprovechamiento analysis={analysis} />
      <span style={{ fontSize: 11.5, color: '#6b7280' }}>
        {efectivo === null ? '· clasificar a mano:' : '· ¿mal clasificada?'}
      </span>
      <div style={{ display: 'flex', gap: 6 }}>
        {opciones.map((o) => {
          const activo = efectivo === o.valor;
          return (
            <button
              key={String(o.valor)}
              type="button"
              disabled={guardando}
              onClick={() => onCorregir(o.valor)}
              style={{
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 600,
                cursor: guardando ? 'wait' : 'pointer',
                border: `1px solid ${activo ? '#818cf8' : '#3f3f4a'}`,
                background: activo ? 'rgba(129, 140, 248, 0.16)' : 'transparent',
                color: activo ? '#c7d2fe' : '#9ca3af',
              }}
            >
              {o.texto}
            </button>
          );
        })}
      </div>
      {corregido && (
        <>
          <button
            type="button"
            disabled={guardando}
            onClick={() => onCorregir(null)}
            style={{
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 11.5,
              cursor: guardando ? 'wait' : 'pointer',
              border: '1px solid #3f3f4a',
              background: 'transparent',
              color: '#9ca3af',
            }}
          >
            Volver al automático
          </button>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            Corregida por {analysis.aprovechable_override_by || 'un coordinador'}
            {analysis.aprovechable_override_at
              ? ` · ${new Date(analysis.aprovechable_override_at).toLocaleDateString('es-MX')}`
              : ''}
          </span>
        </>
      )}
    </div>
  );
}
